/**
 * Reusable Form Modal Component
 *
 * A thin adapter over the design system's `Dialog` that supplies the
 * header/scrolling-body/footer arrangement every admin create-and-edit form
 * uses. It deliberately adds no styling of its own beyond width, which is
 * layout rather than restyling.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@lognai/synapse-design-system-v2';
import { Button } from '@lognai/synapse-design-system-v2';

export interface FormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  onSubmit?: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  isSubmitting?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  hideFooter?: boolean;
}

/** Dialog widths. Layout only — nothing here restyles the Dialog. */
const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function FormModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  submitDisabled = false,
  isSubmitting = false,
  size = 'md',
  hideFooter = false,
}: FormModalProps) {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={sizeClasses[size]}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 py-6 px-1">{children}</div>

        {!hideFooter && (
          <DialogFooter className="flex-shrink-0">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              {cancelLabel}
            </Button>
            {/* Button owns the busy treatment — the hand-rolled spinner this
                replaced hardcoded `border-white`, which vanished against a
                light button and never adapted to the theme. */}
            <Button
              type="button"
              onClick={onSubmit}
              disabled={submitDisabled || isSubmitting}
              isLoading={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : submitLabel}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
